import React, { useEffect, useRef, useState } from "react";
import { CloudUploadIcon, PaperClipIcon } from "@heroicons/react/outline";
import { cn } from "./cn";
import TextLink from "./TextLink";
import Button from "./Button";
import Banner from "./Banner";

/**
 * Bun file dropzone. Spec: Component Library "File upload & attachment row"
 * (also Vault). Dashed boundary, cloud icon, "Drop files or browse" with a
 * blue browse link; the boundary turns blue while a drag hovers it.
 *
 *   <FileDropzone onFiles={(files) => ...} accept=".pdf,.csv,.png" multiple
 *     hint="PDF, CSV, PNG · 25 MB max" />
 *
 * `variant="region"` wraps interactive children without opening a file picker.
 * It owns file-drag feedback and emits drops; pair it with an icon variant for
 * a picker action. `disabled` and `loading` block drops and clear hover feedback.
 * Use `ariaLabel` to name the region and `label` to customize its hover message.
 */
export default function FileDropzone({ onFiles, accept, multiple, hint, className,
  variant = "default", buttonVariant = "primary", label, disabled, loading, ariaLabel, inputLabel, children }) {
  const inputRef = useRef(null);
  const dragDepth = useRef(0);
  const [dragging, setDragging] = useState(false);
  const button = variant === 'button' || variant === 'icon';
  const region = variant === 'region';
  const blocked = disabled || loading;
  useEffect(() => {
    if (!blocked) return;
    dragDepth.current = 0;
    setDragging(false);
  }, [blocked]);
  const hasFiles = (event) => Array.from(event.dataTransfer?.types || []).includes('Files')
    || Array.from(event.dataTransfer?.items || []).some(item => item.kind === 'file')
    || event.dataTransfer?.files?.length > 0;

  const emit = (fileList) => {
    const files = Array.from(fileList || []);
    if (!blocked && files.length && onFiles) onFiles(files);
  };

  return (
    <div
      role={region ? 'group' : undefined}
      aria-label={region ? ariaLabel : undefined}
      onClick={(event) => {
        if (!region && !button && !blocked && !event.target.closest('button,input')) inputRef.current?.click();
      }}
      onDragEnter={variant === 'icon' ? undefined : (e) => {
        if (!hasFiles(e)) return;
        e.preventDefault();
        e.stopPropagation();
        dragDepth.current += 1;
        if (!blocked) setDragging(true);
      }}
      onDragOver={variant === 'icon' ? undefined : (e) => {
        if (!hasFiles(e)) return;
        e.preventDefault();
        e.stopPropagation();
        e.dataTransfer.dropEffect = blocked ? 'none' : 'copy';
        if (!blocked) setDragging(true);
      }}
      onDragLeave={variant === 'icon' ? undefined : (e) => {
        if (!hasFiles(e)) return;
        e.stopPropagation();
        dragDepth.current = Math.max(0, dragDepth.current - 1);
        if (!dragDepth.current && !e.currentTarget.contains(e.relatedTarget)) setDragging(false);
      }}
      onDrop={variant === 'icon' ? undefined : (e) => {
        if (!hasFiles(e)) return;
        e.preventDefault();
        e.stopPropagation();
        dragDepth.current = 0;
        setDragging(false);
        emit(e.dataTransfer.files);
      }}
      className={cn(
        region ? "relative" : button ? "inline-flex" : "border border-dashed rounded-bun-lg p-[18px] font-instrument flex flex-col items-center gap-[6px] text-center cursor-pointer",
        variant === "compact" && "sm:flex-row sm:justify-center sm:gap-[10px]",
        !region && !button && (dragging && !blocked ? "border-bun-blue bg-bun-wash" : "border-bun-line-strong"),
        className
      )}
    >
      {region ? <>
        {dragging && !blocked && <div className="absolute inset-x-0 bottom-full z-10 mb-2"><Banner title={label || "Drop files to upload"} icon={CloudUploadIcon} /></div>}
        {children}
      </> : <><input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        disabled={blocked}
        aria-label={inputLabel || ariaLabel || label || "Choose files"}
        className="hidden"
        onChange={(e) => {
          emit(e.target.files);
          e.target.value = "";
        }}
      />
      {button ? <Button variant={variant === 'icon' ? 'ghost' : buttonVariant} iconOnly={variant === 'icon'} onClick={() => inputRef.current?.click()} disabled={blocked} loading={loading} aria-label={ariaLabel}>
        {variant === 'icon' ? <PaperClipIcon className="h-4 w-4" /> : <><CloudUploadIcon className="h-4 w-4" />{label || "Upload"}</>}
      </Button> : <>
      <CloudUploadIcon className="w-[18px] h-[18px] text-bun-stone" />
      <span className="text-bun-body font-strong text-bun-text">
        {label || "Drop files or"}{" "}
        <TextLink
          as="button"
          disabled={blocked}
          onClick={() => inputRef.current && inputRef.current.click()}
        >
          browse
        </TextLink>
      </span>
      {hint && <span className="text-bun-label tracking-normal font-book text-bun-slate">{hint}</span>}
      </>}
      </>}
    </div>
  );
}
